import React, { useEffect, useState, useCallback } from 'react';
import { 
    CheckCircleIcon, 
    ExclamationCircleIcon, 
    InformationCircleIcon,
    XIcon 
} from '@heroicons/react/outline';

const Toast = ({ 
    id,
    type = 'info', 
    title, 
    message, 
    duration = 5000, 
    onClose,
    className = ''
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);

    const handleClose = useCallback(() => {
        setIsAnimatingOut(true);
        setTimeout(() => {
            setIsVisible(false);
            onClose && onClose(id);
        }, 300); // Match animation duration
    }, [id, onClose]);

    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => {
                handleClose();
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, handleClose]);

    const getTypeConfig = () => {
        switch (type) {
            case 'success':
                return {
                    containerClass: 'bg-green-50 border-green-200',
                    iconClass: 'text-green-400',
                    titleClass: 'text-green-800',
                    messageClass: 'text-green-700',
                    icon: CheckCircleIcon,
                    closeButtonClass: 'text-green-500 hover:text-green-600'
                };
            case 'error':
                return {
                    containerClass: 'bg-red-50 border-red-200',
                    iconClass: 'text-red-400',
                    titleClass: 'text-red-800',
                    messageClass: 'text-red-700',
                    icon: ExclamationCircleIcon,
                    closeButtonClass: 'text-red-500 hover:text-red-600'
                };
            case 'warning':
                return {
                    containerClass: 'bg-yellow-50 border-yellow-200',
                    iconClass: 'text-yellow-400',
                    titleClass: 'text-yellow-800',
                    messageClass: 'text-yellow-700',
                    icon: ExclamationCircleIcon,
                    closeButtonClass: 'text-yellow-500 hover:text-yellow-600'
                };
            case 'info':
            default:
                return {
                    containerClass: 'bg-blue-50 border-blue-200',
                    iconClass: 'text-blue-400',
                    titleClass: 'text-blue-800',
                    messageClass: 'text-blue-700',
                    icon: InformationCircleIcon,
                    closeButtonClass: 'text-blue-500 hover:text-blue-600'
                };
        }
    };

    if (!isVisible) return null;

    const config = getTypeConfig();
    const IconComponent = config.icon;

    return (
        <div
            className={`
                relative flex items-start space-x-3 p-4 border rounded-lg shadow-md mb-3
                transition-all duration-300 ease-in-out
                ${config.containerClass}
                ${isAnimatingOut ? 'opacity-0 transform translate-x-full' : 'opacity-100 transform translate-x-0'}
                ${className}
            `}
        >
            <div className="flex-shrink-0">
                <IconComponent className={`h-5 w-5 ${config.iconClass}`} aria-hidden="true" />
            </div>
            
            <div className="flex-1 min-w-0">
                {title && (
                    <h4 className={`text-sm font-medium ${config.titleClass}`}>
                        {title}
                    </h4>
                )}
                <div className={`text-sm ${config.messageClass} ${title ? 'mt-1' : ''}`}>
                    {message}
                </div>
            </div>
            
            <div className="flex-shrink-0">
                <button
                    type="button"
                    onClick={handleClose}
                    className={`
                        inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2
                        transition-colors duration-200
                        ${config.closeButtonClass}
                    `}
                >
                    <span className="sr-only">Dismiss</span>
                    <XIcon className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

export default Toast;
